import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiWrite, list } from '../lib/api'
import { PageHead, Skel } from '../components/ui'
import { money, number, thaiDate } from '../lib/format'

/**
 * SLA credits (part 131). A contract promises an uptime; a month that misses it earns a
 * credit percentage of the monthly base, capped by the contract. This screen records the
 * month's downtime and shows the exact arithmetic behind the credit note.
 */
interface SLAContract {
  id: string
  name: string
  commitment_bp: number
  monthly_fee_satang: number
  minimum_commit_satang: number
  monthly_cap_satang: number
  status: 'active' | 'archived'
  starts_on: string
  ends_on?: string
  latest_assessment?: SLAAssessment
}

interface SLAAssessment {
  id: string
  contract_id: string
  contract_name?: string
  period: string
  period_start: string
  period_end: string
  computed_at: string
  total_minutes: number
  downtime_minutes: number
  allowed_downtime_minutes: number
  uptime_bp: number
  commitment_bp: number
  breached: boolean
  credit_bp: number
  base_satang: number
  raw_credit_satang: number
  cap_satang: number
  credit_satang: number
  vat_satang: number
  total_satang: number
  capped: boolean
}

const CONTRACTS_KEY = ['sla-contracts'] as const

function percent(bp: number): string {
  return `${(bp / 100).toFixed(2)}%`
}

function currentMonth(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

function parseMinutes(raw: string): number | null {
  const trimmed = raw.trim()
  if (!/^\d+$/.test(trimmed)) return null
  const value = Number(trimmed)
  return Number.isSafeInteger(value) ? value : null
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function statusChip(row: SLAContract): { label: string; color?: string } {
  if (row.status === 'archived') return { label: 'เก็บถาวร' }
  if (row.latest_assessment?.breached) return { label: 'เดือนล่าสุดผิดข้อตกลง', color: 'var(--color-crimson)' }
  if (row.latest_assessment) return { label: 'เดือนล่าสุดเป็นไปตามข้อตกลง', color: 'var(--color-emerald)' }
  return { label: 'ยังไม่มีข้อมูล' }
}

export function SLACredits() {
  const qc = useQueryClient()
  const contracts = useQuery({
    queryKey: CONTRACTS_KEY,
    queryFn: () => list<SLAContract>('/v1/sla/contracts'),
    staleTime: 30_000,
  })

  const rows = contracts.data ?? []
  const [selectedId, setSelectedId] = useState('')
  const selected = rows.find((row) => row.id === selectedId) ?? rows[0]

  const [period, setPeriod] = useState(currentMonth)
  const [downtime, setDowntime] = useState('0')
  const [assessment, setAssessment] = useState<SLAAssessment | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const record = useMutation({
    mutationFn: (input: { id: string; period: string; minutes: number }) =>
      apiWrite<{ assessment: SLAAssessment }>(
        `/v1/sla/contracts/${input.id}/downtime`,
        'POST',
        { period: input.period, downtime_minutes: input.minutes },
      ),
    onSuccess: (res) => {
      setAssessment(res.assessment)
      setNotice(`บันทึก downtime ของเดือน ${res.assessment.period} แล้ว`)
      void qc.invalidateQueries({ queryKey: CONTRACTS_KEY })
    },
  })

  const compute = useMutation({
    mutationFn: (input: { id: string; period: string }) =>
      apiWrite<{ assessment: SLAAssessment }>(
        `/v1/sla/contracts/${input.id}/credits`,
        'POST',
        { period: input.period },
      ),
    onSuccess: (res) => {
      setAssessment(res.assessment)
      setNotice(`คำนวณเครดิตของเดือน ${res.assessment.period} แล้ว`)
      void qc.invalidateQueries({ queryKey: CONTRACTS_KEY })
    },
  })

  function submitDowntime() {
    if (!selected) return
    const minutes = parseMinutes(downtime)
    if (minutes === null) {
      setFormError('จำนวนนาทีต้องเป็นจำนวนเต็มตั้งแต่ 0 ขึ้นไป')
      return
    }
    setFormError(null)
    setNotice(null)
    record.mutate({ id: selected.id, period, minutes })
  }

  function submitCompute() {
    if (!selected) return
    setFormError(null)
    setNotice(null)
    compute.mutate({ id: selected.id, period })
  }

  const busy = record.isPending || compute.isPending
  const activeContracts = rows.filter((row) => row.status === 'active').length
  const breachedLatest = rows.filter((row) => row.latest_assessment?.breached).length

  return (
    <>
      <PageHead
        title="เครดิต SLA"
        sub="วัด uptime รายเดือนจากนาทีที่ระบบไม่พร้อมใช้งาน แล้วคำนวณเครดิตตามขั้นบันไดของสัญญา"
      />

      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <span className="chip">{rows.length} สัญญา</span>
        <span className="chip">{activeContracts} มีผลอยู่</span>
        <span
          className="chip"
          style={breachedLatest > 0 ? { color: 'var(--color-crimson)' } : undefined}
        >
          {breachedLatest} ผิดข้อตกลงในเดือนล่าสุด
        </span>
      </div>

      {notice ? (
        <div
          className="card card-pad"
          role="status"
          style={{ marginBottom: 16, borderColor: 'var(--color-hairline-strong)' }}
        >
          <span className="dim" style={{ fontSize: 12.5 }}>{notice}</span>
        </div>
      ) : null}

      <div className="card card-pad">
        <div className="row" style={{ marginBottom: 12 }}>
          <span className="card-label">สัญญาที่มีผล</span>
          <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
            เลือกสัญญาเพื่อบันทึก downtime หรือคำนวณเครดิต
          </span>
        </div>

        {contracts.isLoading ? <Skel h={220} /> : contracts.isError ? (
          <div className="form-error">{errorText(contracts.error, 'โหลดสัญญา SLA ไม่สำเร็จ')}</div>
        ) : rows.length === 0 ? (
          <div className="dim" style={{ fontSize: 12.5, lineHeight: 1.8 }}>
            ยังไม่มีสัญญา SLA · เมื่อมีสัญญาที่มีผล รายการจะปรากฏที่นี่พร้อมข้อตกลงและเครดิตเดือนล่าสุด
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table data">
              <thead>
                <tr>
                  <th>สัญญา</th>
                  <th style={{ width: 120 }}>ข้อตกลง uptime</th>
                  <th style={{ width: 150, textAlign: 'right' }}>ค่าบริการรายเดือน</th>
                  <th style={{ width: 150, textAlign: 'right' }}>เพดานเครดิต</th>
                  <th style={{ width: 160 }}>เดือนล่าสุด</th>
                  <th style={{ width: 150, textAlign: 'right' }}>เครดิตเดือนล่าสุด</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const chip = statusChip(row)
                  return (
                    <tr
                      key={row.id}
                      onClick={() => {
                        setSelectedId(row.id)
                        setAssessment(row.latest_assessment ?? null)
                        setNotice(null)
                        setFormError(null)
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div>{row.name}</div>
                        <div className="dim" style={{ fontSize: 11 }}>
                          เริ่ม {thaiDate(row.starts_on)}
                          {row.ends_on ? ` · สิ้นสุด ${thaiDate(row.ends_on)}` : ''}
                        </div>
                      </td>
                      <td className="tnum">{percent(row.commitment_bp)}</td>
                      <td className="tnum" style={{ textAlign: 'right' }}>{money(row.monthly_fee_satang)}</td>
                      <td className="tnum" style={{ textAlign: 'right' }}>{money(row.monthly_cap_satang)}</td>
                      <td>
                        {row.latest_assessment ? (
                          <>
                            <div className="tnum">{row.latest_assessment.period}</div>
                            <div className="dim tnum" style={{ fontSize: 11 }}>
                              uptime {percent(row.latest_assessment.uptime_bp)}
                            </div>
                          </>
                        ) : (
                          <span className="dim" style={{ fontSize: 11.5 }}>ยังไม่คำนวณ</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {row.latest_assessment ? (
                          <span className={row.latest_assessment.breached ? 'tnum' : 'tnum dim'}>
                            {money(row.latest_assessment.credit_satang)}
                          </span>
                        ) : (
                          <span className="dim">—</span>
                        )}
                        <div className="dim" style={{ fontSize: 11, marginTop: 2 }}>{chip.label}</div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected ? (
        <div className="grid-2" style={{ marginTop: 22, alignItems: 'start' }}>
          <div className="card card-pad">
            <div className="card-label">บันทึกความพร้อมใช้งาน</div>
            <div className="dim" style={{ fontSize: 12, lineHeight: 1.8, marginTop: 8 }}>
              {selected.name} · ข้อตกลง {percent(selected.commitment_bp)} ·
              ฐานเครดิตคือค่าที่มากกว่าระหว่างค่าบริการกับข้อผูกพันขั้นต่ำ
            </div>

            <div className="field" style={{ marginTop: 16 }}>
              <label htmlFor="sla-period">เดือน</label>
              <input
                id="sla-period"
                className="input"
                type="month"
                value={period}
                onChange={(event) => setPeriod(event.target.value)}
              />
            </div>

            <div className="field" style={{ marginTop: 12 }}>
              <label htmlFor="sla-downtime">นาทีที่ระบบไม่พร้อมใช้งานทั้งเดือน</label>
              <input
                id="sla-downtime"
                className="input tnum"
                inputMode="numeric"
                value={downtime}
                onChange={(event) => setDowntime(event.target.value)}
              />
              <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>
                บันทึกทับยอดเดิมของเดือนนั้น ไม่ใช่บวกเพิ่ม · หนึ่งนาทีคือหน่วยที่เล็กที่สุด
              </div>
            </div>

            {formError ? <div className="form-error" style={{ marginTop: 12 }}>{formError}</div> : null}

            <div className="row" style={{ gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
              <button className="btn btn-primary" onClick={submitDowntime} disabled={busy}>
                {record.isPending ? 'กำลังบันทึก…' : 'บันทึก downtime'}
              </button>
              <button className="btn" onClick={submitCompute} disabled={busy}>
                {compute.isPending ? 'กำลังคำนวณ…' : 'คำนวณเครดิต'}
              </button>
            </div>

            {record.isError ? (
              <div className="form-error" style={{ marginTop: 12 }}>
                {errorText(record.error, 'บันทึก downtime ไม่สำเร็จ')}
              </div>
            ) : null}
            {compute.isError ? (
              <div className="form-error" style={{ marginTop: 12 }}>
                {errorText(compute.error, 'คำนวณเครดิตไม่สำเร็จ')}
              </div>
            ) : null}
          </div>

          <div className="card card-pad">
            <div className="row" style={{ marginBottom: 12 }}>
              <span className="card-label">ผลการคำนวณ</span>
              {assessment ? (
                <span
                  className="chip"
                  style={{
                    marginLeft: 'auto',
                    color: assessment.breached ? 'var(--color-crimson)' : 'var(--color-emerald)',
                  }}
                >
                  {assessment.breached ? 'ผิดข้อตกลง' : 'เป็นไปตามข้อตกลง'}
                </span>
              ) : null}
            </div>

            {assessment ? (
              <>
                <div className="row" style={{ gap: 24, flexWrap: 'wrap' }}>
                  <div>
                    <div className="dim" style={{ fontSize: 11 }}>uptime ที่วัดได้</div>
                    <div className="stat-value tnum" style={{ fontSize: 28 }}>{percent(assessment.uptime_bp)}</div>
                    <div className="dim" style={{ fontSize: 11 }}>
                      ข้อตกลง {percent(assessment.commitment_bp)} · อนุญาต {number(assessment.allowed_downtime_minutes)} นาที
                    </div>
                  </div>
                  <div>
                    <div className="dim" style={{ fontSize: 11 }}>ขั้นเครดิต</div>
                    <div className="stat-value tnum" style={{ fontSize: 28 }}>{percent(assessment.credit_bp)}</div>
                    <div className="dim" style={{ fontSize: 11 }}>
                      downtime {number(assessment.downtime_minutes)} จาก {number(assessment.total_minutes)} นาที
                    </div>
                  </div>
                </div>

                <div className="kv" style={{ marginTop: 16 }}>
                  <span>ฐานคำนวณ (ค่าบริการหรือข้อผูกพันขั้นต่ำ)</span>
                  <span className="tnum">{money(assessment.base_satang)}</span>
                </div>
                <div className="kv">
                  <span>เครดิตก่อนเพดาน</span>
                  <span className="tnum">{money(assessment.raw_credit_satang)}</span>
                </div>
                <div className="kv">
                  <span>เพดานรายเดือนของสัญญา</span>
                  <span className="tnum">{money(assessment.cap_satang)}</span>
                </div>
                {assessment.capped ? (
                  <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>
                    เครดิตก่อนเพดานสูงกว่าเพดาน จึงจ่ายตามเพดานของเดือนนี้
                  </div>
                ) : null}
                <div className="kv" style={{ marginTop: 8 }}>
                  <span>เครดิตที่ใช้ได้</span>
                  <span className="tnum">{money(assessment.credit_satang)}</span>
                </div>
                <div className="kv">
                  <span>VAT 7%</span>
                  <span className="tnum">{money(assessment.vat_satang)}</span>
                </div>
                <div className="kv">
                  <span><strong>ยอดเครดิตสุทธิ</strong></span>
                  <span className="tnum"><strong>{money(assessment.total_satang)}</strong></span>
                </div>
                <div className="dim" style={{ fontSize: 11, marginTop: 12 }}>
                  คำนวณเมื่อ {thaiDate(assessment.computed_at)} · เดือน {assessment.period} ·
                  รอบถัดไปเริ่ม {thaiDate(assessment.period_end)}
                </div>
              </>
            ) : (
              <div className="dim" style={{ fontSize: 12.5, lineHeight: 1.8 }}>
                เลือกเดือนและกด “คำนวณเครดิต” · ระบบจะอ่าน downtime ที่บันทึกไว้ของเดือนนั้น
                แล้วแสดง uptime ขั้นเครดิต และเพดานที่ใช้บังคับ
              </div>
            )}
          </div>
        </div>
      ) : null}

      {selected ? (
        <div className="card card-pad" style={{ marginTop: 22 }}>
          <div className="card-label">ขั้นบันไดเครดิตของสัญญานี้</div>
          <div className="dim" style={{ fontSize: 12, lineHeight: 1.8, marginTop: 8 }}>
            แต่ละขั้นคิดจากส่วนต่างระหว่าง uptime ที่วัดได้กับข้อตกลง {percent(selected.commitment_bp)}
            แล้วคูณกับฐานเครดิต ก่อนตัดด้วยเพดาน {money(selected.monthly_cap_satang)} ต่อเดือน
          </div>
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="table data">
              <thead>
                <tr>
                  <th>ช่วง uptime</th>
                  <th style={{ width: 140, textAlign: 'right' }}>สัดส่วนเครดิต</th>
                  <th style={{ width: 180, textAlign: 'right' }}>เครดิตสูงสุดก่อนเพดาน</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ cursor: 'default' }}>
                  <td className="tnum">≥ {percent(selected.commitment_bp)}</td>
                  <td className="tnum" style={{ textAlign: 'right' }}>0%</td>
                  <td className="tnum" style={{ textAlign: 'right' }}>{money(0)}</td>
                </tr>
                {[
                  { from: 100, to: 1, bp: 1000 },
                  { from: 500, to: 101, bp: 2500 },
                  { from: 1000, to: 501, bp: 5000 },
                  { from: 10000, to: 1001, bp: 10000 },
                ].map((tier) => {
                  const low = Math.max(0, selected.commitment_bp - tier.from)
                  const high = selected.commitment_bp - tier.to
                  const base = Math.max(selected.monthly_fee_satang, selected.minimum_commit_satang)
                  return (
                    <tr key={tier.from} style={{ cursor: 'default' }}>
                      <td className="tnum">{percent(low)} – {percent(high)}</td>
                      <td className="tnum" style={{ textAlign: 'right' }}>{percent(tier.bp)}</td>
                      <td className="tnum" style={{ textAlign: 'right' }}>
                        {money(Math.min((base * tier.bp) / 10000, selected.monthly_cap_satang))}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </>
  )
}
